import React from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';
import ElementLink from '../ElementLink/ElementLink';
import styles from './Breadcrumb.module.scss';
import { useEffect, useState } from 'react';
import { useRef } from 'react';

export const BreadcrumbPropTypes = {
  breadcrumbs: PropTypes.arrayOf(PropTypes.object).isRequired
};

function Breadcrumb({ breadcrumbs, parentPage }) {
  const { t } = useTranslation();
  const [hideMiddle, setHideMiddle] = useState(false); // hide middle breadcrumbs as dots when needed
  const [forceExpanded, setForceExpanded] = useState(false); // once dots are clicked, keep all items visible and wrapped
  const learnRef = useRef();
  const topicsRef = useRef([]);
  const listRef = useRef();
  let currentPosition = 1;
  const baseBreadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': currentPosition,
        'item': {
          '@id': `${process.env.HOST}/`,
          'name': t('common:textBreadcrumbRoot')
        }
      }
    ]
  };

  // Track if overflow was ever detected to persist collapsed state
  const hasOverflowedRef = useRef(false);

  useEffect(() => {
    if (!listRef.current) return;

    const recalc = () => {
      // if user explicitly expanded, do not auto-hide again
      if (forceExpanded) {
        setHideMiddle(false);
        return;
      }

      const listEl = listRef.current;
      if (!listEl) return;

      // Get the true available width - use the breadcrumb wrapper's width
      const wrapper = listEl.closest(`.${styles.breadcrumbWrap}`) || listEl.parentElement;
      const availableWidth = wrapper ? wrapper.getBoundingClientRect().width : window.innerWidth;

      // Temporarily remove any overflow/text-overflow CSS to measure true content width
      const originalOverflow = listEl.style.overflow;
      const originalWhiteSpace = listEl.style.whiteSpace;
      const originalTextOverflow = listEl.style.textOverflow;
      const originalMaxWidth = listEl.style.maxWidth;
      const originalWidth = listEl.style.width;

      listEl.style.overflow = 'visible';
      listEl.style.whiteSpace = 'nowrap';
      listEl.style.textOverflow = 'clip';
      listEl.style.maxWidth = 'none';
      listEl.style.width = 'auto';

      // Measure total content width of all items
      let totalContentWidth = 0;
      const allItems = listEl.querySelectorAll('li');
      allItems.forEach((li) => {
        const rect = li.getBoundingClientRect();
        totalContentWidth += rect.width;
      });

      // Restore original styles
      listEl.style.overflow = originalOverflow;
      listEl.style.whiteSpace = originalWhiteSpace;
      listEl.style.textOverflow = originalTextOverflow;
      listEl.style.maxWidth = originalMaxWidth;
      listEl.style.width = originalWidth;

      // Add buffer for separator icons and gaps (estimate ~20px per item for slashes/gaps)
      const itemCount = allItems.length;
      const gapBuffer = itemCount > 0 ? (itemCount - 1) * 8 : 0;
      const totalWidth = totalContentWidth + gapBuffer;

      // Check if content overflows the container (with small tolerance)
      const isOverflowing = totalWidth > availableWidth - 10;

      // Once overflow is detected, remember it
      if (isOverflowing) {
        hasOverflowedRef.current = true;
      }

      // Show dots if currently overflowing OR if it ever overflowed (and user hasn't expanded)
      const shouldHide = (isOverflowing || hasOverflowedRef.current) && breadcrumbs && breadcrumbs.length >= 2;

      setHideMiddle(shouldHide);
    };

    // Delay initial calculation to ensure fonts and layout are ready
    const initialTimeout = setTimeout(recalc, 150);

    // Also recalc after a longer delay to catch late layout shifts
    const secondTimeout = setTimeout(recalc, 500);

    // Also recalc after fonts are loaded
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(recalc, 100);
      });
    }

    window.addEventListener('resize', recalc);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(secondTimeout);
      window.removeEventListener('resize', recalc);
    };
  }, [breadcrumbs, forceExpanded]);

  // Reset overflow tracking when breadcrumbs change
  useEffect(() => {
    hasOverflowedRef.current = false;
  }, [breadcrumbs]);

  const handleDotsClick = () => {
    // reveal middle breadcrumbs again and keep them visible and allow wrapping
    setHideMiddle(false);
    setForceExpanded(true);
  };

  const breadCrumbClasses = `${styles.breadCrumb} ${styles.hide} ${forceExpanded ? styles.breadCrumbExpanded : ''}`;

  return (
    <div className={styles.breadcrumb}>
      <div className={styles.breadcrumbWrap}>
        <nav aria-label="Breadcrumbs" data-testid="Breadcrumb" className={parentPage ? styles.breadCrumbContainer : ""}>
          <ol className={breadCrumbClasses} ref={listRef}>
            <li className={styles.firstItem} ref={learnRef}>
              <ElementLink
                href="/"
                as="/"
                isModal={false}
                className={styles.breadcrumbLink}
                trackingId={`breadcrumb_item_home`}
                download={false}
                linkText={t('common:textBreadcrumbRoot')}
                data-testid="Breadcrumb-linkOneText">
                {t('common:textBreadcrumbRoot')}
              </ElementLink>
            </li>

            {breadcrumbs && breadcrumbs.length
              ? breadcrumbs.map((breadcrumb, i) => {
                const isLast = breadcrumbs.length === i + 1;

                baseBreadcrumbSchema.itemListElement.push({
                  '@type': 'ListItem',
                  'position': currentPosition,
                  'item': {
                    '@id': `${process.env.HOST}/${breadcrumb.as}`,
                    'name': breadcrumb.text
                  }
                });

                // middle items are those that are not the last one when we have at least 2 items after home
                const hasMiddleItems = breadcrumbs.length > 1;
                const isMiddle = hasMiddleItems && !isLast;

                // when overflowing, collapse all middle items into a single dots element rendered at the first middle index
                const shouldRenderDots = hideMiddle && isMiddle && i === 0 && hasMiddleItems;
                const shouldHideAsMiddle = hideMiddle && isMiddle && i !== 0;

                if (shouldRenderDots) {
                  return (
                    <li
                      className={` ${styles.lastBreadcrumb} ${styles.slashIcon}`}
                      aria-hidden="false"
                      key={`breadcrumb-dots-${breadcrumb.as}`}>
                      <span
                        className={styles.dotted}
                        onClick={handleDotsClick}
                        role="button"
                        tabIndex={0}
                        aria-label={t('common:textShowFullBreadcrumb') || 'Show full breadcrumb'}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDotsClick(); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.00041 6.66667C7.82166 6.65228 7.64192 6.6769 7.47363 6.73882C7.30533 6.80074 7.15249 6.89848 7.02569 7.02528C6.89889 7.15208 6.80114 7.30492 6.73923 7.47322C6.67731 7.64151 6.65269 7.82125 6.66708 8C6.65269 8.17875 6.67731 8.35849 6.73923 8.52679C6.80114 8.69508 6.89889 8.84792 7.02569 8.97472C7.15249 9.10153 7.30533 9.19927 7.47363 9.26119C7.64192 9.32311 7.82166 9.34772 8.00041 9.33333C8.17916 9.34772 8.3589 9.32311 8.5272 9.26119C8.69549 9.19927 8.84833 9.10153 8.97513 8.97472C9.10194 8.84792 9.19968 8.69508 9.2616 8.52679C9.32351 8.35849 9.34813 8.17875 9.33374 8C9.34813 7.82125 9.32351 7.64151 9.2616 7.47322C9.19968 7.30492 9.10194 7.15208 8.97513 7.02528C8.84833 6.89848 8.69549 6.80074 8.5272 6.73882C8.3589 6.6769 8.17916 6.65228 8.00041 6.66667Z" fill="#736C64"></path>
                          <path d="M12.0004 6.66667C11.8217 6.65228 11.6419 6.6769 11.4736 6.73882C11.3053 6.80074 11.1525 6.89848 11.0257 7.02528C10.8989 7.15208 10.8011 7.30492 10.7392 7.47322C10.6773 7.64151 10.6527 7.82125 10.6671 8C10.6527 8.17875 10.6773 8.35849 10.7392 8.52679C10.8011 8.69508 10.8989 8.84792 11.0257 8.97472C11.1525 9.10153 11.3053 9.19927 11.4736 9.26119C11.6419 9.32311 11.8217 9.34772 12.0004 9.33333C12.1792 9.34772 12.3589 9.32311 12.5272 9.26119C12.6955 9.19927 12.8483 9.10153 12.9751 8.97472C13.1019 8.84792 13.1997 8.69508 13.2616 8.52679C13.3235 8.35849 13.3481 8.17875 13.3337 8C13.3481 7.82125 13.3235 7.64151 13.2616 7.47322C13.1997 7.30492 13.1019 7.15208 12.9751 7.02528C12.8483 6.89848 12.6955 6.80074 12.5272 6.73882C12.3589 6.6769 12.1792 6.65228 12.0004 6.66667Z" fill="#736C64"></path>
                          <path d="M4.00041 6.66667C3.82166 6.65228 3.64192 6.6769 3.47362 6.73882C3.30533 6.80074 3.15249 6.89848 3.02569 7.02528C2.89889 7.15208 2.80114 7.30492 2.73923 7.47322C2.67731 7.64151 2.65269 7.82125 2.66708 8C2.65269 8.17875 2.67731 8.35849 2.73923 8.52679C2.80114 8.69508 2.89889 8.84792 3.02569 8.97472C3.15249 9.10153 3.30533 9.19927 3.47362 9.26119C3.64192 9.32311 3.82166 9.34772 4.00041 9.33333C4.17916 9.34772 4.3589 9.32311 4.5272 9.26119C4.69549 9.19927 4.84833 9.10153 4.97513 8.97472C5.10194 8.84792 5.19968 8.69508 5.2616 8.52679C5.32351 8.35849 5.34813 8.17875 5.33374 8C5.34813 7.82125 5.32351 7.64151 5.2616 7.47322C5.19968 7.30492 5.10194 7.15208 4.97513 7.02528C4.84833 6.89848 4.69549 6.80074 4.5272 6.73882C4.3589 6.6769 4.17916 6.65228 4.00041 6.66667Z" fill="#736C64"></path>
                        </svg>
                        <span className={styles.slashbar}></span>
                      </span>
                    </li>
                  );
                }

                if (shouldHideAsMiddle) {
                  return null;
                }
                
                return (
                  <li
                    className={` ${styles.lastBreadcrumb} ${styles.slashIcon} ${isLast ? styles.lastEllipsis : ''}`}
                    aria-current={isLast ? 'page' : undefined}
                    ref={(el) => (topicsRef.current[i] = el)}
                    key={`breadrumb-link-${breadcrumb.as}`}>
                    <ElementLink
                      href={breadcrumb.href}
                      as={breadcrumb.as}
                      trackingId={`breadcrumb_item_${breadcrumb.href}`}
                      isModal={false}
                      className={`${styles.breadcrumbLink} ${isLast ? styles.breadcrumbLinkLast : ''}`}
                      download={false}
                      parent="breadcrumb"
                      linkText={(breadcrumbs.length == 1 || breadcrumbs.length === 2 || breadcrumbs.length == 3)
                        ? breadcrumb.text === "Self-guided learning"
                          ? t('common:selfguidedLearning')
                          : breadcrumb.text === "Video library"
                            ? t('common:videolibrary')
                            : breadcrumb.text
                        : undefined}
                      slashIcon={"slashIcon"}
                      slashIconClassName={styles.slashIcon}
                      data-testid="Breadcrumb-linkOneText">
                      {breadcrumb.text}
                    </ElementLink>
                  </li>
                );
              })
              : null}
          </ol>
        </nav>

        {/* eslint-disable-next-line react/no-danger */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(baseBreadcrumbSchema) }} />
      </div>
    </div >
  );
}

Breadcrumb.propTypes = BreadcrumbPropTypes;

export default Breadcrumb;